import { useState, useEffect, useCallback, useRef } from "react";
import { T } from "../../theme";
import { apiCallEx } from "../../apiClient";
import { Btn, Badge } from "../primitives";

// ── GlobalAiConsole (operator) — catalog tier temperatures + platform default keys ──
//
// The operator control plane for platform AI routing: per-(tier, role) provider +
// model assignment, the GLOBAL per-tier temperature (applies to every customer with
// no per-tenant override), and the KontractAI platform default keys (validate-on-save,
// write-only, never shown again). Every mutation hits /platform/ai-routing/* or
// /platform/ai-default-key, all of which sit behind the backend `operatorOnly` gate —
// so who may READ/WRITE here is enforced server-side (full operator, OR the walls-down
// in-house `admin.manage_ai_routing` holder), NOT by where this component is rendered.
// This component MUST therefore only be mounted for is_operator || is_ai_routing_operator
// callers (see AiRoutingAdmin / PlatformConsole); the server is the backstop, this is
// the defense-in-depth render gate.
//
// This was extracted verbatim from PlatformConsole's "Global AI" modal so that both
// the full operator console (PlatformConsole, walls-up) and the consolidated
// Admin ▸ AI & Models ▸ AI Routing screen (AiRoutingAdmin, walls-down in-house) render
// ONE implementation — no divergence, no duplicated logic.
//
// Props:
//   onDirtyChange?  — called whenever unsaved provider/model picks appear/clear, so a
//                     hosting Modal can guard its close. (Tab usage passes nothing.)
//   onRequestClose? — when provided, a "Done" footer button is rendered that calls it.
//                     The dirty-discard confirm lives in the PARENT's close handler.
export const GlobalAiConsole = ({
  onDirtyChange,
  onRequestClose,
}: {
  onDirtyChange?: (dirty: boolean) => void;
  onRequestClose?: () => void;
}) => {
  const AI_PROVIDERS = ["anthropic", "openai", "groq"] as const;
  // Audit reason carried on every operator call (mirrors the operator console's
  // reason= query param). Static — the surface itself is the reason.
  const REASON = "AI routing console";
  const rq = (reason: string) => `reason=${encodeURIComponent(reason)}`;
  // TEMPERATURE CAPABILITY IS NOT GUESSED IN THE BROWSER. Every model descriptor from
  // GET /ai-routing/providers carries a backend-supplied `supports_temperature`, and
  // every tier row from GET /ai-routing/tiers carries one for its saved model. The
  // backend `anthropicSupportsTemperature` remains AUTHORITATIVE for what is actually
  // sent to a provider; the value read here governs RENDERING only.
  // MIRROR: backend/src/services/aiRouter.js `anthropicSupportsTemperature`.
  const DEFAULT_TIER_TEMPERATURE = "0.2";

  const [globalTiers, setGlobalTiers] = useState<any[]>([]);
  const [globalTempInput, setGlobalTempInput] = useState<Record<string, string>>({});
  const [defaultKeys, setDefaultKeys] = useState<Record<string, boolean>>({});
  // Read-only "which key copy is actually live" indicator: the effective platform
  // default key state the RUNNING app resolves (its process.env), never the value.
  const [defaultKeyEff, setDefaultKeyEff] = useState<Record<string, { live: boolean; is_placeholder: boolean; length: number; source: string }>>({});
  const [defaultKeyInput, setDefaultKeyInput] = useState<Record<string, string>>({});
  const [globalBusy, setGlobalBusy] = useState<string | null>(null);
  const [globalErr, setGlobalErr] = useState(""); const [globalMsg, setGlobalMsg] = useState("");
  // Provider catalog + per-row pending provider/model selection (dirty state).
  const [globalProviders, setGlobalProviders] = useState<any[]>([]);
  const [globalTierMeta, setGlobalTierMeta] = useState<Record<string, any>>({});
  const [globalRoleChain, setGlobalRoleChain] = useState<any[]>([]);
  const [globalSkipRules, setGlobalSkipRules] = useState<string[]>([]);
  const [globalMetaOpen, setGlobalMetaOpen] = useState(false);
  const [gtProviderInput, setGtProviderInput] = useState<Record<string, string>>({});
  const [gtModelInput, setGtModelInput] = useState<Record<string, string>>({});
  const [gtRowErr, setGtRowErr] = useState<Record<string, string>>({});
  const [gtTempPrefilled, setGtTempPrefilled] = useState<Record<string, boolean>>({});
  // Live model discovery (operator-triggered; never automatic).
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshResult, setRefreshResult] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);

  const gtKey = (t: any) => `${t.tier}:${t.role}`;
  // Providers the backend `upsertTier` would accept: active, and ZDR satisfied.
  const selectableProviders = globalProviders.filter((p) => p.is_active && !(p.zdr_required && !p.zdr_enabled));
  const providerById = (id: any) => globalProviders.find((p) => String(p.id) === String(id));
  const pendingProviderId = (t: any) => gtProviderInput[gtKey(t)] ?? String(t.provider_id);
  const pendingModelId = (t: any) => gtModelInput[gtKey(t)] ?? String(t.model_id || "");
  const modelDescriptor = (prov: any, modelId: string): any =>
    (Array.isArray(prov?.available_models) ? prov.available_models : []).find((m: any) => m.id === modelId);
  const rowSupportsTemp = (t: any): boolean => {
    const desc = modelDescriptor(providerById(pendingProviderId(t)), pendingModelId(t));
    if (desc && typeof desc.supports_temperature === "boolean") return desc.supports_temperature;
    return t.supports_temperature !== false;
  };
  const rowTempUnverified = (t: any): boolean => {
    const desc = modelDescriptor(providerById(pendingProviderId(t)), pendingModelId(t));
    return !!desc && desc.temperature_verified === false && desc.supports_temperature === true;
  };
  const rowDirty = (t: any) =>
    String(pendingProviderId(t)) !== String(t.provider_id) || pendingModelId(t) !== String(t.model_id || "");
  const anyRowDirty = () => globalTiers.some(rowDirty);

  const loadGlobal = useCallback(async (reason: string) => {
    const [tiersRes, keysRes, provRes, metaRes] = await Promise.all([
      apiCallEx(`/platform/ai-routing/tiers?${rq(reason)}`),
      apiCallEx(`/platform/ai-default-key?${rq(reason)}`),
      apiCallEx(`/platform/ai-routing/providers?${rq(reason)}`),
      apiCallEx(`/platform/ai-routing/tier-metadata?${rq(reason)}`),
    ]);
    if (tiersRes.ok && Array.isArray(tiersRes.data?.tiers)) {
      setGlobalTiers(tiersRes.data.tiers);
      const init: Record<string, string> = {};
      const pi: Record<string, string> = {}; const mi: Record<string, string> = {};
      for (const t of tiersRes.data.tiers) {
        init[gtKey(t)] = t.temperature_default != null ? String(t.temperature_default) : "";
        pi[gtKey(t)] = String(t.provider_id); mi[gtKey(t)] = String(t.model_id || "");
      }
      setGlobalTempInput(init); setGtProviderInput(pi); setGtModelInput(mi); setGtRowErr({});
    } else setGlobalErr("Could not load the routing catalog.");
    if (keysRes.ok && Array.isArray(keysRes.data?.providers)) {
      const m: Record<string, boolean> = {};
      const eff: Record<string, { live: boolean; is_placeholder: boolean; length: number; source: string }> = {};
      for (const p of keysRes.data.providers) { m[p.provider] = !!p.configured; if (p.effective) eff[p.provider] = p.effective; }
      setDefaultKeys(m); setDefaultKeyEff(eff);
    }
    if (provRes.ok && Array.isArray(provRes.data?.providers)) setGlobalProviders(provRes.data.providers);
    if (metaRes.ok && metaRes.data?.metadata) {
      setGlobalTierMeta(metaRes.data.metadata);
      setGlobalRoleChain(Array.isArray(metaRes.data.role_chain) ? metaRes.data.role_chain : []);
      setGlobalSkipRules(Array.isArray(metaRes.data.role_skip_rules) ? metaRes.data.role_skip_rules : []);
    }
    setLoaded(true);
  }, []);

  // Load once on mount. (The hosting surface decides WHEN to mount us — the modal
  // mounts on open; the tab mounts when selected.)
  useEffect(() => { loadGlobal(REASON); }, [loadGlobal]);

  // Report dirty state to a hosting Modal so it can guard its close.
  useEffect(() => { if (onDirtyChange) onDirtyChange(anyRowDirty()); });

  const onPickProvider = (t: any, providerId: string) => {
    const k = gtKey(t);
    setGtProviderInput({ ...gtProviderInput, [k]: providerId });
    setGtModelInput({ ...gtModelInput, [k]: String(t.provider_id) === String(providerId) ? String(t.model_id || "") : "" });
    setGtRowErr({ ...gtRowErr, [k]: "" });
  };
  const onPickModel = (t: any, modelId: string) => {
    const k = gtKey(t);
    setGtModelInput({ ...gtModelInput, [k]: modelId });
    setGtRowErr({ ...gtRowErr, [k]: "" });
    const desc = modelDescriptor(providerById(pendingProviderId(t)), modelId);
    const willSupport = desc && typeof desc.supports_temperature === "boolean"
      ? desc.supports_temperature
      : t.supports_temperature !== false;
    if (willSupport && !(globalTempInput[k] || "").trim()) {
      setGlobalTempInput({ ...globalTempInput, [k]: DEFAULT_TIER_TEMPERATURE });
      setGtTempPrefilled({ ...gtTempPrefilled, [k]: true });
    } else if (!willSupport) {
      setGtTempPrefilled({ ...gtTempPrefilled, [k]: false });
    }
  };
  const saveGlobalTier = async (t: any) => {
    const k = gtKey(t);
    const provId = pendingProviderId(t); const modelId = pendingModelId(t);
    if (!modelId) { setGtRowErr({ ...gtRowErr, [k]: "Pick a model for the selected provider." }); return; }
    const prov = providerById(provId);
    const supports = rowSupportsTemp(t);
    const raw = (globalTempInput[k] || "").trim();
    const n = Number(raw);
    if (supports && (raw === "" || !Number.isFinite(n) || n < 0 || n > 1)) {
      setGtRowErr({ ...gtRowErr, [k]: "Global temperature must be a number between 0 and 1." }); return;
    }
    const body: any = {
      provider_id: Number(provId), model_id: modelId,
      max_tokens_default: t.max_tokens_default,
      temperature_default: supports ? n : (t.temperature_default != null ? Number(t.temperature_default) : undefined),
      reason: REASON,
    };
    setGlobalBusy(k + "temp"); setGlobalErr(""); setGlobalMsg(""); setGtRowErr({ ...gtRowErr, [k]: "" });
    const r = await apiCallEx(`/platform/ai-routing/tiers/${t.tier}/${t.role}`, { method: "PUT", body: JSON.stringify(body) });
    setGlobalBusy(null);
    if (r.ok) { setGlobalMsg(`Tier ${t.tier} ${t.role}: now ${prov?.name || provId} / ${modelId}${supports ? ` at temperature ${n}` : " (reasoning model — no temperature sent)"}.`); loadGlobal(REASON); }
    else setGtRowErr({ ...gtRowErr, [k]: r.data?.error || "Could not save this tier assignment." });
  };
  const refreshModels = async () => {
    setRefreshBusy(true); setGlobalErr(""); setGlobalMsg(""); setRefreshResult(null);
    const r = await apiCallEx(`/platform/ai-routing/providers/refresh-models`, {
      method: "POST", body: JSON.stringify({ reason: REASON }),
    });
    if (r.ok && r.data) {
      setRefreshResult(r.data);
      await loadGlobal(REASON);
    } else {
      setGlobalErr(r.data?.error || "Could not refresh the model lists.");
    }
    setRefreshBusy(false);
  };
  const saveDefaultKey = async (provider: string) => {
    const key = (defaultKeyInput[provider] || "").trim();
    if (!key) { setGlobalErr("Enter a key to set."); return; }
    setGlobalBusy(provider + "key"); setGlobalErr(""); setGlobalMsg("");
    const r = await apiCallEx(`/platform/ai-default-key`, { method: "PUT", body: JSON.stringify({ provider, api_key: key, reason: REASON }) });
    setGlobalBusy(null);
    if (r.ok && r.data?.success) { setDefaultKeyInput({ ...defaultKeyInput, [provider]: "" }); setGlobalMsg(`${provider} platform default key validated and saved.`); loadGlobal(REASON); }
    else setGlobalErr(r.data?.message || (r.data?.error === "invalid_key" ? "That key could not be validated with the provider and was not saved." : "Could not save the default key."));
  };
  const clearDefaultKey = async (provider: string) => {
    setGlobalBusy(provider + "key"); setGlobalErr(""); setGlobalMsg("");
    const r = await apiCallEx(`/platform/ai-default-key?provider=${provider}&${rq(REASON)}`, { method: "DELETE" });
    setGlobalBusy(null);
    if (r.ok) { setGlobalMsg(`${provider} platform default key removed.`); loadGlobal(REASON); }
    else setGlobalErr("Could not remove the default key.");
  };

  return (
    <div>
      <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>
        Operator-only. Assign the <strong>provider and model</strong> for each tier + role, edit the <strong>global</strong>
        per-tier temperature (applies to every customer that has no per-tenant override), and manage the KontractAI
        <strong> platform default keys</strong> (validated on save, write-only, never shown again). Removing a default
        key makes that provider fall closed until re-staged. Models are <strong>global</strong> — every customer runs on
        what you set here, and the per-tenant AI keys reflect it immediately.
      </p>
      {globalErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{globalErr}</div>}
      {globalMsg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{globalMsg}</div>}

      <div style={{ fontSize: 11, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", margin: "4px 0 8px" }}>Platform default keys</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
        {AI_PROVIDERS.map((p) => (
          <div key={p} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 90, fontSize: 13, color: T.tx, textTransform: "capitalize" }}>{p}</div>
            {defaultKeys[p] ? <Badge c={T.grn} b={T.grnS}>saved</Badge> : <Badge c={T.txD} b={T.bg4}>not set</Badge>}
            {(() => { const e = defaultKeyEff[p]; if (!e) return null;
              if (e.live) return <Badge c={T.grn} b={T.grnS}>{`live · ${e.source === "platform_override" ? "operator key" : "KV base"} · ${e.length} chars`}</Badge>;
              if (e.is_placeholder) return <Badge c={T.red} b={T.bg4}>placeholder — NOT live</Badge>;
              return <Badge c={T.txD} b={T.bg4}>not effective</Badge>;
            })()}
            <input type="password" autoComplete="off" value={defaultKeyInput[p] || ""} onChange={e => setDefaultKeyInput({ ...defaultKeyInput, [p]: e.target.value })} placeholder={defaultKeys[p] ? "•••••• (enter to replace)" : "enter key"} style={{ flex: 1, minWidth: 180, padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace" }} />
            <Btn v="primary" sz="sm" disabled={globalBusy === p + "key" || !(defaultKeyInput[p] || "").trim()} onClick={() => saveDefaultKey(p)}>{globalBusy === p + "key" ? "…" : "Save"}</Btn>
            <Btn v="ghost" sz="sm" disabled={globalBusy === p + "key" || !defaultKeys[p]} onClick={() => clearDefaultKey(p)}>Remove</Btn>
          </div>
        ))}
      </div>

      {/* ── What each tier is for + which tasks route to it (read-only) ── */}
      <div style={{ marginBottom: 16, border: `1px solid ${T.brd}`, borderRadius: 6 }}>
        <button onClick={() => setGlobalMetaOpen(!globalMetaOpen)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", background: "none", border: "none", cursor: "pointer", color: T.txD, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>
          <span>What the tiers mean · how the fallback chain works</span>
          <span style={{ color: T.txM }}>{globalMetaOpen ? "▾ hide" : "▸ show"}</span>
        </button>
        {globalMetaOpen && (
          <div style={{ padding: "0 10px 10px" }}>
            {Object.keys(globalTierMeta).length === 0 ? (
              <div style={{ fontSize: 12, color: T.txM, padding: 6 }}>Loading tier metadata…</div>
            ) : ["1", "2", "3"].filter(k => globalTierMeta[k]).map((k) => {
              const m = globalTierMeta[k];
              return (
                <div key={k} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${T.brd}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: T.txM, margin: "3px 0 6px", lineHeight: 1.5 }}>{m.description}</div>
                  <div style={{ fontSize: 11.5, color: T.txM }}>
                    <strong style={{ color: T.txD }}>Surfaces on this tier:</strong>{" "}
                    {(m.surfaces || []).map((s: any) => s.name).join(" · ") || "none"}
                  </div>
                  <div style={{ fontSize: 11.5, color: T.txM, marginTop: 3 }}>
                    <strong style={{ color: T.txD }}>Registered tasks (ai_task_routing):</strong>{" "}
                    {(m.tasks || []).length === 0
                      ? <span style={{ fontStyle: "italic" }}>none registered — this tier is dispatched by tier number, not by task name</span>
                      : (m.tasks || []).map((t: any) => t.task_name).join(" · ")}
                  </div>
                </div>
              );
            })}
            {globalRoleChain.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Primary → fallback → tertiary</div>
                {globalRoleChain.map((r: any) => (
                  <div key={r.role} style={{ fontSize: 11.5, color: T.txM, marginBottom: 3, lineHeight: 1.5 }}>
                    <strong style={{ color: T.txD, textTransform: "capitalize" }}>{r.role}:</strong> {r.summary}
                  </div>
                ))}
                {globalSkipRules.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5, color: T.txM, lineHeight: 1.5 }}>
                    {globalSkipRules.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, margin: "4px 0 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em" }}>Routing catalog · provider, model and global temperature per tier + role</div>
        <Btn v="secondary" sz="sm" disabled={refreshBusy} onClick={refreshModels}>{refreshBusy ? "Refreshing…" : "Refresh model lists"}</Btn>
      </div>
      {refreshBusy && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.bg4, color: T.txM, fontSize: 12 }}>Querying each provider's model list… this can take up to 30 seconds per provider.</div>}

      {/* ── Discovery result: per-provider summary + the loud in-use warning ── */}
      {refreshResult && (
        <div style={{ marginBottom: 12 }}>
          {Array.isArray(refreshResult.in_use_missing) && refreshResult.in_use_missing.length > 0 && (
            <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `2px solid ${T.red}` }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ A model still assigned to a tier is no longer offered by its provider</div>
              {refreshResult.in_use_missing.map((m: any, i: number) => (
                <div key={i} style={{ fontFamily: "monospace", fontSize: 12 }}>Tier {m.tier} · {m.role} → {m.provider}/{m.model_id}</div>
              ))}
              <div style={{ marginTop: 6 }}>These assignments were <strong>left exactly as they were</strong> — nothing was reassigned. Calls on those roles will keep using the model until you change it. Decide per row.</div>
            </div>
          )}
          {(refreshResult.providers || []).map((p: any) => (
            <div key={p.provider} style={{ marginBottom: 6, padding: "8px 10px", borderRadius: 6, border: `1px solid ${p.ok ? T.brd : T.red}`, backgroundColor: p.ok ? undefined : T.redS, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: p.ok ? T.tx : T.red, textTransform: "capitalize" }}>
                {p.provider} — {p.ok ? `${p.counts.added} added · ${p.counts.removed} removed · ${p.counts.retained} retained · ${p.counts.excluded} excluded` : "refresh failed"}
              </div>
              {!p.ok && <div style={{ color: T.red, marginTop: 3 }}>{p.error} <span style={{ color: T.txM }}>(other providers were unaffected)</span></div>}
              {p.ok && p.added?.length > 0 && <div style={{ color: T.grn, marginTop: 3 }}>added: <span style={{ fontFamily: "monospace" }}>{p.added.join(", ")}</span></div>}
              {p.ok && p.removed?.length > 0 && <div style={{ color: T.ylw, marginTop: 3 }}>removed: <span style={{ fontFamily: "monospace" }}>{p.removed.join(", ")}</span></div>}
              {p.ok && p.unverified_temperature?.length > 0 && <div style={{ color: T.ylw, marginTop: 3 }}>temperature support assumed (unverified): <span style={{ fontFamily: "monospace" }}>{p.unverified_temperature.join(", ")}</span></div>}
              {p.ok && p.excluded?.length > 0 && (
                <details style={{ marginTop: 4, color: T.txM }}>
                  <summary style={{ cursor: "pointer" }}>{p.excluded.length} excluded — why</summary>
                  {p.excluded.map((e: any, i: number) => <div key={i} style={{ fontSize: 11.5 }}><span style={{ fontFamily: "monospace" }}>{e.id}</span> — {e.reason}</div>)}
                </details>
              )}
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: T.txM }}>Tier assignments were not modified by this refresh — only the list of models you can choose from.</div>
        </div>
      )}
      {!loaded ? (
        <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 12 }}>Loading catalog…</div>
      ) : globalTiers.length === 0 ? (
        <div style={{ textAlign: "center", color: T.txM, fontSize: 13, padding: 12 }}>No routing tiers configured.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {globalTiers.map((t) => {
            const k = gtKey(t);
            const provId = pendingProviderId(t);
            const prov = providerById(provId);
            const modelId = pendingModelId(t);
            // Operator shortlist: only models NOT flagged hidden are selectable.
            const models: any[] = (Array.isArray(prov?.available_models) ? prov.available_models : []).filter((m: any) => m && m.hidden !== true);
            const supports = rowSupportsTemp(t);
            const tempUnverified = rowTempUnverified(t);
            const dirty = rowDirty(t);
            const isAnthropic = String(prov?.name || "").toLowerCase().includes("anthropic");
            const warns: string[] = [];
            if (modelId && !isAnthropic) {
              warns.push("Streaming works on this provider, but drafting's extended-thinking (Anthropic-only) is dropped here — off-Claude drafting quality is unverified and may be reduced.");
              warns.push("Documents (PDF) are pre-extracted to text and images work on vision-capable models; a raw image sent to a text-only model surfaces a clear limitation instead of being silently dropped.");
            }
            if (modelId && prov && !prov.supports_tools) warns.push("This provider has tool use disabled — tool-use requests skip this role.");
            const tierRows = globalTiers.filter((x) => String(x.tier) === String(t.tier));
            const tierHasTools = tierRows.some((x) => providerById(pendingProviderId(x))?.supports_tools);
            const tierHasAnthropic = tierRows.some((x) => String(providerById(pendingProviderId(x))?.name || "").toLowerCase().includes("anthropic"));
            const isFirstRowOfTier = tierRows.length > 0 && gtKey(tierRows[0]) === k;
            return (
              <div key={k} style={{ padding: "8px 10px", border: `1px solid ${dirty ? T.ylw : T.brd}`, borderRadius: 6, backgroundColor: dirty ? T.ylwS : undefined }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ width: 120, flexShrink: 0, fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: T.tx }}>Tier {t.tier}</span>
                    <span style={{ color: T.txM }}> · {t.role}</span>
                  </div>
                  <select value={String(provId)} onChange={e => onPickProvider(t, e.target.value)} style={{ width: 150, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12.5 }}>
                    {!selectableProviders.some(p => String(p.id) === String(provId)) && <option value={String(provId)}>{t.provider_name} (unavailable)</option>}
                    {selectableProviders.map((p) => <option key={p.id} value={String(p.id)}>{p.display_name || p.name}</option>)}
                  </select>
                  <select value={modelId} onChange={e => onPickModel(t, e.target.value)} style={{ flex: 1, minWidth: 190, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12.5, fontFamily: "monospace" }}>
                    <option value="">— pick a model —</option>
                    {modelId && !models.some((m: any) => m.id === modelId) && (
                      <option value={modelId}>{modelId} (no longer listed)</option>
                    )}
                    {models.map((m: any) => <option key={m.id} value={m.id}>{m.label && m.label !== m.id ? `${m.id} — ${m.label}` : m.id}</option>)}
                  </select>
                  {supports ? (
                    <input type="number" step="0.05" min={0} max={1} value={globalTempInput[k] ?? ""} onChange={e => setGlobalTempInput({ ...globalTempInput, [k]: e.target.value })} title="Global temperature" style={{ width: 76, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13 }} />
                  ) : (
                    <span style={{ width: 76, fontSize: 10.5, color: T.txD, fontStyle: "italic", textAlign: "center" }}>no temp</span>
                  )}
                  <Btn v="primary" sz="sm" disabled={globalBusy === k + "temp" || !modelId} onClick={() => saveGlobalTier(t)}>{globalBusy === k + "temp" ? "…" : "Save"}</Btn>
                  {dirty && <Badge c={T.ylw} b={T.ylwS}>unsaved</Badge>}
                </div>
                {gtRowErr[k] && <div style={{ marginTop: 5, fontSize: 11.5, color: T.red }}>{gtRowErr[k]}</div>}
                {gtTempPrefilled[k] && supports && <div style={{ marginTop: 4, fontSize: 11, color: T.txM }}>Temperature prefilled with the schema default {DEFAULT_TIER_TEMPERATURE} — not saved until you press Save.</div>}
                {tempUnverified && <div style={{ marginTop: 4, fontSize: 11, color: T.ylw }}>⚠ Temperature support for this model is assumed, not confirmed. It matches no known reasoning-model prefix; a live check is warranted before relying on it.</div>}
                {warns.map((w, i) => <div key={i} style={{ marginTop: 4, fontSize: 11, color: T.ylw }}>⚠ {w}</div>)}
                {isFirstRowOfTier && !tierHasTools && <div style={{ marginTop: 4, fontSize: 11, color: T.red }}>⚠ No role on Tier {t.tier} supports tool use — tool-use calls on this tier will fail with NO_COMPATIBLE_PROVIDERS.</div>}
                {isFirstRowOfTier && !tierHasAnthropic && <div style={{ marginTop: 4, fontSize: 11, color: T.red }}>⚠ No Anthropic role on Tier {t.tier} — streaming surfaces on this tier cannot run at all.</div>}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
        <span style={{ fontSize: 11.5, color: anyRowDirty() ? T.ylw : "transparent" }}>Unsaved provider/model changes — Save each row to apply.</span>
        {onRequestClose && <Btn v="secondary" onClick={onRequestClose}>Done</Btn>}
      </div>
    </div>
  );
};
